import React from 'react';
import { parseISO, getDay, format } from 'date-fns';

/** Standard app-wide date display format: 15-Jan-2025 */
export const formatDate = (iso) => iso ? format(parseISO(iso), 'dd-MMM-yyyy') : '—';

/**
 * Minimum total_work_minutes for a working day to count as fully worked.
 * 555 minutes = 9h15m.
 */
export const REQUIRED_WORKDAY_MINUTES = 555;

/**
 * Shared utility to consistently render attendance status badges across the dashboards
 */
export const getStatusBadge = (record) => {
  // Gracefully handle if record or workday is missing
  if (!record || !record.workday) return null;

  const dayOfWeek = getDay(parseISO(record.workday));
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6

  const badges = [];

  // 1. Primary Attendance State
  if (record.leave_code) {
    badges.push(<span key="leave" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">{record.leave_code}</span>);
  } else if (record.holiday_name) {
    badges.push(<span key="holiday" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">{record.holiday_name}</span>);
  } else if (isWeekend && !record.clock_in && !record.clock_out) {
    badges.push(<span key="weekend" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Weekend</span>);
  } else if (!record.clock_in && !record.clock_out) {
    badges.push(<span key="pending" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Pending / Missed</span>);
  } else if (record.clock_in && !record.clock_out) {
    // Has clock-in but no clock-out — incomplete record
    if (record.is_late) {
      badges.push(<span key="late" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Late</span>);
    } else {
      badges.push(<span key="incomplete" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">No Clock Out</span>);
    }
  } else if (record.is_late && record.is_early_out) {
    badges.push(<span key="late-early" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Late & Early Out</span>);
  } else if (record.is_late) {
    badges.push(<span key="late" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Late</span>);
  } else if (record.is_early_out) {
    badges.push(<span key="early" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Early Out</span>);
  } else {
    badges.push(<span key="ontime" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">On Time</span>);
  }

  // 1b. Insufficient Hours — fully punched working day that still falls short
  // of a full workday. Independent of, and can co-occur with, late/early-out
  // (e.g. on time in and out, but with an uncaptured long break in between).
  if (
    record.clock_in && record.clock_out &&
    !record.leave_code && !record.holiday_name && !isWeekend &&
    record.total_work_minutes < REQUIRED_WORKDAY_MINUTES
  ) {
    badges.push(<span key="insufficient" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">Insufficient Hours</span>);
  }

  // 2. Additional Meta Tags
  if (record.is_overridden) {
    badges.push(<span key="excused" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">Excused</span>);
  }
  if (record.is_final) {
    badges.push(<span key="final" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Finalized</span>);
  }

  return (
    <div className="flex flex-col space-y-1 items-start">
      {badges}
    </div>
  );
};

/**
 * Shared utility for consistent row highlighting (Sunday Red, Finalized Gray)
 */
export const getRowHighlightClass = (workday, is_final = false) => {
  if (!workday) return is_final ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50';
  
  const dayOfWeek = getDay(parseISO(workday));
  if (dayOfWeek === 0) {
    return 'bg-red-50 hover:bg-red-100'; // Sunday
  }
  
  return is_final ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50';
};

/**
 * Format total work minutes as h:mm (e.g. 8:05, 10:30).
 * Returns '-' for zero or missing values.
 */
export const formatMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) return '-';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

/**
 * Shared utility to cleanly format workday string to display Date and Day of Week
 */
export const DateColumnDisplay = ({ workday }) => {
  if (!workday) return <div className="text-sm font-medium text-gray-900">-</div>;
  const parsed = parseISO(workday);
  return (
    <div>
      <div className="text-sm font-medium text-gray-900">{format(parsed, 'dd-MMM-yyyy')}</div>
      <div className="text-xs text-gray-500">{format(parsed, 'EEEE')}</div>
    </div>
  );
};
